import * as React from "react";

import { cn } from "@/lib/utils";
import { Badge } from "./badge";
import { Trophy } from "lucide-react";

const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("rounded-lg border bg-card text-card-foreground shadow-sm", className)} {...props} />
));
Card.displayName = "Card";

interface CardComparisonProps extends React.HTMLAttributes<HTMLDivElement> {
  isHighlighted?: boolean;
  bestValue?: boolean;
  bestValueLabel?: string;
}

const CardComparison = React.forwardRef<HTMLDivElement, CardComparisonProps>(
  ({ className, isHighlighted, bestValue, bestValueLabel = "Bäst i test", children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-lg border bg-card text-card-foreground shadow-sm relative transition-colors",
        isHighlighted && "ring-1 ring-primary z-10",
        className
      )}
      {...props}
    >
      {bestValue && (
        <Badge variant="premium" className="absolute -top-3 left-1/2 -translate-x-1/2 border-0">
          <Trophy className="w-3 h-3 mr-1" />
          {bestValueLabel}
        </Badge>
      )}
      {children}
    </div>
  )
);
CardComparison.displayName = "CardComparison";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn("text-2xl font-semibold leading-none tracking-tight", className)} {...props} />
  ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />,
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export { Card, CardComparison, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
